import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useAuth } from "@/context/AuthContext";
import { Box, Checkbox, Chip, Button, Divider, FormControlLabel, IconButton, Paper, Snackbar, Stack, Tooltip, Typography } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import { alpha } from "@mui/material/styles";
import { status, gray, blue, green } from "@/theme/colors";
import { useTrafficTracker } from "@/hooks/useTrafficTracker";
import { trafficTracker, PAGE_LOAD_TARGETS } from "@/utils/trafficTracker";
import { clearIndexCaches } from "@/utils/localStore";

/**
 * Global floating control for the traffic tracker. Rendered app-wide (from
 * _app.tsx) but only visible once the user enables tracking from the profile
 * modal. Record / pause, clear the in-progress session, and save it.
 */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export default function TrafficTrackerPill() {
  const tracker = useTrafficTracker();
  const [toast, setToast] = useState(null);
  const router = useRouter();

  // The tracker is an admin dogfood tool: if the session is not an
  // actively signed-in admin (logged out, expired, or demoted), flip the
  // setting back off so the pill never renders or records for them.
  const {
    user,
    initializing
  } = useAuth();
  useEffect(() => {
    if (initializing) return;
    if (tracker.enabled && user?.role !== "admin") {
      trafficTracker.setEnabled(false);
    }
  }, [initializing, user, tracker.enabled]);

  // Page-load mode measures index pages as the user navigates: feed
  // route changes (and the initial mount) to the tracker.
  useEffect(() => {
    // Open the measurement window on routeChangeStart, not Complete: the
    // index pages read their cache in a mount effect that runs before the
    // transition completes, so a window opened on Complete would miss
    // every read and never capture the "cached" phase.
    trafficTracker.notePageNav(window.location.pathname);
    trafficTracker.notePageNavDone();
    const onStart = url => trafficTracker.notePageNav(url.split("?")[0]);
    const onDone = () => trafficTracker.notePageNavDone();
    router.events.on("routeChangeStart", onStart);
    router.events.on("routeChangeComplete", onDone);
    return () => {
      router.events.off("routeChangeStart", onStart);
      router.events.off("routeChangeComplete", onDone);
    };
  }, [router.events]);

  // Render-guard in addition to the effect above: without it the pill
  // would flash for one paint before the effect flips the setting off.
  if (!tracker.enabled || initializing || user?.role !== "admin") {
    return null;
  }
  const {
    recording,
    totals,
    activeEvents,
    byUrl
  } = tracker;
  const eventCount = activeEvents.length;
  const handleSave = () => {
    const saved = tracker.saveSession();
    setToast(saved ? `Saved "${saved.name}" — view it in Admin → Analytics → Tracked sessions` : "Nothing to save yet");
  };
  const breakdown = `${totals.backend} backend · ${totals.cacheReads} reads (${totals.cacheHits} hit) · ${totals.cacheWrites} writes · ${totals.versionChecks} version · ${totals.merges} merge`;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Paper, {
      elevation: 8,
      "data-select-target": "Traffic Tracker Pill",
      sx: {
        position: "fixed",
        bottom: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1250,
        px: 1.5,
        py: 0.75,
        borderRadius: 999,
        display: "flex",
        alignItems: "center",
        gap: 1,
        border: "1px solid",
        borderColor: recording ? alpha(status.error, 0.5) : "divider",
        maxWidth: "calc(100vw - 24px)"
      },
      children: [/*#__PURE__*/_jsx(Tooltip, {
        title: "Clears the data caches, then measures each page once uncached (initial) and once cached. Visit home, organizations, events, regulations, articles, and professionals \u2014 twice each.",
        arrow: true,
        children: /*#__PURE__*/_jsx(FormControlLabel, {
          control: /*#__PURE__*/_jsx(Checkbox, {
            size: "small",
            checked: tracker.pageLoadMode,
            onChange: e => {
              if (e.target.checked) {
                // Force true initial loads for every
                // data page.
                clearIndexCaches();
                try {
                  window.localStorage.removeItem("pc_data_versions_token_v1");
                } catch {}
                trafficTracker.setPageLoadMode(true);
                setToast("Page-load capture armed — visit home and each directory page twice (initial, then cached).");
              } else {
                trafficTracker.setPageLoadMode(false);
              }
            }
          }),
          label: "Page load",
          sx: {
            mr: 0,
            ml: -0.5,
            "& .MuiFormControlLabel-label": {
              fontSize: 12.5,
              fontWeight: 600
            }
          }
        })
      }), tracker.pageLoadMode && /*#__PURE__*/_jsxs(Typography, {
        sx: {
          fontSize: 12,
          color: "text.secondary",
          whiteSpace: "nowrap"
        },
        children: [tracker.pageLoads.length, "/", PAGE_LOAD_TARGETS.length * 2, " captured"]
      }), tracker.pageLoadComplete && tracker.lastPageLoadSessionId && /*#__PURE__*/_jsx(Button, {
        size: "small",
        variant: "contained",
        sx: {
          py: 0,
          textTransform: "none"
        },
        onClick: () => window.open(`/Admin?tab=analytics&sessions=open&session=${tracker.lastPageLoadSessionId}`, "_blank", "noopener"),
        children: "View Result"
      }), /*#__PURE__*/_jsx(Divider, {
        orientation: "vertical",
        flexItem: true,
        sx: {
          my: 0.5
        }
      }), /*#__PURE__*/_jsx(Tooltip, {
        title: recording ? "Recording traffic" : "Recording paused",
        children: /*#__PURE__*/_jsx(FiberManualRecordIcon, {
          sx: {
            fontSize: 14,
            color: recording ? status.error : gray.primary,
            animation: recording ? "tt-pulse 1.4s ease-in-out infinite" : "none",
            "@keyframes tt-pulse": {
              "0%,100%": {
                opacity: 1
              },
              "50%": {
                opacity: 0.3
              }
            }
          }
        })
      }), /*#__PURE__*/_jsx(Tooltip, {
        title: breakdown,
        arrow: true,
        children: /*#__PURE__*/_jsxs(Stack, {
          sx: {
            minWidth: 0
          },
          children: [/*#__PURE__*/_jsxs(Typography, {
            sx: {
              fontSize: 12.5,
              fontWeight: 700,
              lineHeight: 1.15,
              whiteSpace: "nowrap"
            },
            children: [eventCount, " events", /*#__PURE__*/_jsxs(Typography, {
              component: "span",
              sx: {
                fontSize: 12.5,
                fontWeight: 400,
                color: "text.secondary"
              },
              children: [" ", "\xB7 ", byUrl.length, " page", byUrl.length === 1 ? "" : "s"]
            })]
          }), /*#__PURE__*/_jsxs(Stack, {
            direction: "row",
            spacing: 0.5,
            sx: {
              mt: 0.25
            },
            children: [/*#__PURE__*/_jsx(MiniStat, {
              label: "be",
              value: totals.backend,
              color: blue.primary
            }), /*#__PURE__*/_jsx(MiniStat, {
              label: "hit",
              value: totals.cacheHits,
              color: green.primary
            }), /*#__PURE__*/_jsx(MiniStat, {
              label: "wr",
              value: totals.cacheWrites,
              color: gray.dark
            })]
          })]
        })
      }), /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center"
        },
        children: [/*#__PURE__*/_jsx(Tooltip, {
          title: recording ? "Pause" : "Record",
          children: /*#__PURE__*/_jsx(IconButton, {
            size: "small",
            onClick: () => tracker.setRecording(!recording),
            sx: {
              color: recording ? status.error : green.primary
            },
            children: recording ? /*#__PURE__*/_jsx(PauseIcon, {
              fontSize: "small"
            }) : /*#__PURE__*/_jsx(PlayArrowIcon, {
              fontSize: "small"
            })
          })
        }), /*#__PURE__*/_jsx(Tooltip, {
          title: "Clear this session's events",
          children: /*#__PURE__*/_jsx("span", {
            children: /*#__PURE__*/_jsx(IconButton, {
              size: "small",
              onClick: () => tracker.clearActive(),
              disabled: eventCount === 0,
              children: /*#__PURE__*/_jsx(RestartAltIcon, {
                fontSize: "small"
              })
            })
          })
        }), /*#__PURE__*/_jsx(Tooltip, {
          title: "Pause & save session",
          children: /*#__PURE__*/_jsx("span", {
            children: /*#__PURE__*/_jsx(IconButton, {
              size: "small",
              onClick: handleSave,
              disabled: eventCount === 0,
              sx: {
                color: blue.primary
              },
              children: /*#__PURE__*/_jsx(SaveIcon, {
                fontSize: "small"
              })
            })
          })
        })]
      })]
    }), /*#__PURE__*/_jsx(Snackbar, {
      open: !!toast,
      autoHideDuration: 4000,
      onClose: () => setToast(null),
      message: toast ?? "",
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center"
      },
      sx: {
        mb: 9
      }
    })]
  });
}
function MiniStat({
  label,
  value,
  color
}) {
  return /*#__PURE__*/_jsx(Chip, {
    label: `${value} ${label}`,
    size: "small",
    sx: {
      height: 16,
      fontSize: 10,
      fontWeight: 600,
      bgcolor: alpha(color, 0.14),
      color,
      "& .MuiChip-label": {
        px: 0.6
      }
    }
  });
}